import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Checklist as CountIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import stockCountService from '../../../../services/stockCountService';
import venueLocationService from '../../../../services/venueLocationService';
import type { StockCount } from '../../../../types/venue';

const formatDate = (d: string | null | undefined): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const StockCountList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId, venueName } = useVenue();
  const base = `/admin/venues/${venueId}/stock-counts`;

  const [counts, setCounts] = useState<StockCount[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locationNames, setLocationNames] = useState<Map<string, string>>(new Map());
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadCounts = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await stockCountService.getCounts(venueId, page + 1, pageSize);
      setCounts(result.data);
      setTotal(result.pagination.totalItems);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load stock counts');
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Load location names for display — non-blocking
  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(locs => {
        const map = new Map<string, string>();
        locs.forEach(l => map.set(l._id, l.name));
        setLocationNames(map);
      })
      .catch(() => {});
  }, [venueId]);

  const handleDelete = async (countId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this draft count? This cannot be undone.')) return;
    setDeleting(countId);
    try {
      await stockCountService.deleteCount(venueId, countId);
      loadCounts();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to delete count');
    } finally {
      setDeleting(null);
    }
  };

  const scopeLabel = (count: StockCount): string => {
    if (!count.locationId) return venueName;
    return locationNames.get(count.locationId) ?? count.locationId;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CountIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>Stock Counts</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`${base}/create`)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            New Stock Count
          </Button>
        </Box>

        {error && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Loading stock counts…</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Scope', 'Count Date', 'Counted By', 'Posted', 'Status', ''].map(h => (
                      <TableCell
                        key={h}
                        sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.75rem', textTransform: 'uppercase' }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {counts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ py: 8, textAlign: 'center', color: theme.palette.text.secondary }}>
                          <CountIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body2">No stock counts yet. Start by creating a new count.</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    counts.map(count => (
                      <TableRow
                        key={count._id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`${base}/${count._id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{scopeLabel(count)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(count.countedAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{count.countedBy || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{formatDate(count.postedAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={count.status === 'posted' ? 'Posted' : 'Draft'}
                            size="small"
                            color={count.status === 'posted' ? 'success' : 'default'}
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {count.status === 'draft' && (
                            <Tooltip title="Delete draft">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={e => handleDelete(count._id, e)}
                                  disabled={deleting === count._id}
                                  sx={{ color: theme.palette.text.disabled }}
                                >
                                  {deleting === count._id
                                    ? <CircularProgress size={16} />
                                    : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default StockCountList;
